import React, { useContext, useState } from "react";
import { useCart } from "./cartContext";
import { Link } from "react-router-dom";
import {
  User,
  MapPin,
  CreditCard,
  ArrowLeft,
  ChevronLeft,
  Check as CheckIcon,
  Shield,
} from "lucide-react";
import { CurrencyContext } from "./CurrencyContext";

export default function Checkout() {
  const { cartItems, clearCart } = useCart();
  const { currencySign, convertPrice } = useContext(CurrencyContext);

  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId] = useState("ORD-" + Date.now());

  // Step 1: Contact
  const [contactInfo, setContactInfo] = useState({ email: "", phone: "" });
  const [contactErrors, setContactErrors] = useState({});

  // Step 2: Shipping
  const [shippingAddress, setShippingAddress] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });
  const [shippingErrors, setShippingErrors] = useState({});

  // Step 3: Payment
  const [cardInfo, setCardInfo] = useState({
    number: "",
    name: "",
    expiry: "",
    cvv: "",
  });
  const [cardErrors, setCardErrors] = useState({});

  // Totals
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
    0
  );
  const shipping = cartItems.length > 0 ? 9.99 : 0;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  // Steps
  const steps = [
    { id: 1, name: "Contact", icon: User },
    { id: 2, name: "Shipping", icon: MapPin },
    { id: 3, name: "Payment", icon: CreditCard },
    { id: 4, name: "Review", icon: CheckIcon },
  ];

  // Validation functions
  const validateContact = () => {
    const errors = {};
    if (!contactInfo.email) errors.email = "Email is required";
    else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(contactInfo.email))
      errors.email = "Invalid email address";

    if (!contactInfo.phone) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(contactInfo.phone))
      errors.phone = "Phone must be 10 digits";

    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateShipping = () => {
    const errors = {};
    if (!shippingAddress.firstName) errors.firstName = "First name is required";
    if (!shippingAddress.lastName) errors.lastName = "Last name is required";
    if (!shippingAddress.address) errors.address = "Address is required";
    if (!shippingAddress.city) errors.city = "City is required";
    if (!shippingAddress.state) errors.state = "State is required";
    if (!shippingAddress.zipCode) errors.zipCode = "Zip code is required";
    else if (!/^\d{5}$/.test(shippingAddress.zipCode))
      errors.zipCode = "Invalid zip code";

    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCard = () => {
    const errors = {};
    if (!cardInfo.number) errors.number = "Card number is required";
    else if (!/^\d{16}$/.test(cardInfo.number))
      errors.number = "Invalid card number";

    if (!cardInfo.name) errors.name = "Cardholder name is required";
    if (!cardInfo.expiry) errors.expiry = "Expiry date is required";
    if (!cardInfo.cvv) errors.cvv = "CVV is required";
    else if (!/^\d{3,4}$/.test(cardInfo.cvv)) errors.cvv = "Invalid CVV";

    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    let valid = true;
    if (currentStep === 1) valid = validateContact();
    if (currentStep === 2) valid = validateShipping();
    if (currentStep === 3);
    if (valid && currentStep < steps.length) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const handlePlaceOrder = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setOrderComplete(true);
      clearCart();
    }, 2000);
  };

  // Empty Cart
  if (cartItems.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <button className="mt-3">
            <Link
              to="/"
              className="bg-[#fe7590] text-white px-4 py-2 rounded-xl hover:bg-red-700"
            >
              Continue Shopping
            </Link>
          </button>
        </div>
      </div>
    );
  }

  // Order complete
  if (orderComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
          <p className="text-gray-600 mb-6">
            Your order id is <strong className="text-black">{orderId}</strong>
          </p>
          <Link
            to="/"
            className="bg-[#fe7590] text-white px-6 py-3 rounded-xl hover:bg-blue-700"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link
            to="/cart"
            className="flex items-center text-gray-700 hover:text-[#fe7590] cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Cart
          </Link>
          <div className="flex items-center text-gray-600 text-sm">
            <Shield className="w-4 h-4 mr-1" /> SSL Secured
          </div>
        </div>
      </header>

      {/* Steps */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between max-w-3xl mx-auto">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                        ? "bg-green-600 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckIcon className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium ${
                      isActive
                        ? "text-[#fe7590]"
                        : isCompleted
                        ? "text-green-600"
                        : "text-gray-600"
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 transition-colors duration-300 ${
                      isCompleted ? "bg-green-600" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Forms */}
      <div className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        {/* Step 1: Contact */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
            <input
              type="email"
              placeholder="Email"
              value={contactInfo.email}
              onChange={(e) =>
                setContactInfo({ ...contactInfo, email: e.target.value })
              }
              className={`w-full border px-3 py-2 rounded focus:outline-none ${
                contactErrors.email ? "border-[#fe7590]" : "border-gray-300"
              }`}
            />
            {contactErrors.email && (
              <p className="text-[#fe7590] text-sm">{contactErrors.email}</p>
            )}
            <input
              type="text"
              placeholder="Phone"
              value={contactInfo.phone}
              onChange={(e) =>
                setContactInfo({ ...contactInfo, phone: e.target.value })
              }
              className={`w-full border px-3 py-2 rounded focus:outline-none ${
                contactErrors.phone ? "border-[#fe7590]" : "border-gray-300"
              }`}
            />
            {contactErrors.phone && (
              <p className="text-[#fe7590] text-sm">{contactErrors.phone}</p>
            )}
            <div className="flex justify-between mt-4">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="flex items-center px-4 py-2 border rounded hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </button>
              <button
                onClick={handleNext}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Shipping */}
        {currentStep === 2 && (
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  value={shippingAddress.firstName}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      firstName: e.target.value,
                    })
                  }
                  className={`border px-3 py-2 rounded w-full ${
                    shippingErrors.firstName
                      ? "border-[#fe7590]"
                      : "border-gray-300"
                  }`}
                />
                {shippingErrors.firstName && (
                  <p className="text-[#fe7590] text-sm">
                    {shippingErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Last Name"
                  value={shippingAddress.lastName}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      lastName: e.target.value,
                    })
                  }
                  className={`border px-3 py-2 rounded w-full ${
                    shippingErrors.lastName
                      ? "border-[#fe7590]"
                      : "border-gray-300"
                  }`}
                />
                {shippingErrors.lastName && (
                  <p className="text-[#fe7590] text-sm">
                    {shippingErrors.lastName}
                  </p>
                )}
              </div>
            </div>
            <input
              type="text"
              placeholder="Address"
              value={shippingAddress.address}
              onChange={(e) =>
                setShippingAddress({
                  ...shippingAddress,
                  address: e.target.value,
                })
              }
              className={`w-full border px-3 py-2 rounded ${
                shippingErrors.address ? "border-[#fe7590]" : "border-gray-300"
              }`}
            />
            {shippingErrors.address && (
              <p className="text-[#fe7590] text-sm">{shippingErrors.address}</p>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="City"
                  value={shippingAddress.city}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      city: e.target.value,
                    })
                  }
                  className={`border px-3 py-2 rounded w-full ${
                    shippingErrors.city ? "border-[#fe7590]" : "border-gray-300"
                  }`}
                />
                {shippingErrors.city && (
                  <p className="text-[#fe7590] text-sm">{shippingErrors.city}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="State"
                  value={shippingAddress.state}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      state: e.target.value,
                    })
                  }
                  className={`border px-3 py-2 rounded w-full ${
                    shippingErrors.state ? "border-[#fe7590]" : "border-gray-300"
                  }`}
                />
                {shippingErrors.state && (
                  <p className="text-[#fe7590] text-sm">{shippingErrors.state}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Zip Code"
                  value={shippingAddress.zipCode}
                  onChange={(e) =>
                    setShippingAddress({
                      ...shippingAddress,
                      zipCode: e.target.value,
                    })
                  }
                  className={`border px-3 py-2 rounded w-full ${
                    shippingErrors.zipCode
                      ? "border-[#fe7590]"
                      : "border-gray-300"
                  }`}
                />
                {shippingErrors.zipCode && (
                  <p className="text-[#fe7590] text-sm">
                    {shippingErrors.zipCode}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 border rounded hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </button>
              <button
                onClick={handleNext}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h2 className="text-xl font-semibold mb-4">Payment Information</h2>
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-lg shadow-md mt-4 w-full max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-2">Payment Mode</h3>
              <p className="text-gray-700">
                Currently, the only payment option available is{" "}
                <span className="font-bold text-gray-900">
                  Cash on Delivery (COD)
                </span>
                .
              </p>
            </div>

            <div className="flex justify-between mt-4">
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 border rounded hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </button>
              <button
                onClick={handleNext}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h2 className="text-xl font-semibold mb-4">Review Your Order</h2>

            <div className="space-y-2">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between border-b pb-2"
                >
                  <span>
                    {item.name} x {item.quantity}
                  </span>
                  <span>
                    {currencySign}
                    {convertPrice((item.price * item.quantity).toFixed(2))}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-1">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>
                  {currencySign}
                  {convertPrice(subtotal.toFixed(2))}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>
                  {currencySign}
                  {convertPrice(shipping.toFixed(2))}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span>
                  {currencySign}
                  {convertPrice(tax.toFixed(2))}
                </span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>
                  {currencySign}
                  {convertPrice(total.toFixed(2))}
                </span>
              </div>
            </div>

            <div className="flex justify-between mt-4">
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 border rounded hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </button>
              <button
                onClick={handlePlaceOrder}
                disabled={isProcessing}
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 flex items-center justify-center"
              >
                {isProcessing ? "Processing..." : "Place Order"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
